import React, { useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  OutlinedInput,
  Radio,
  RadioGroup,
  TextField
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';

type Option = { label: string; value: number };

type SelectWithSearchModalProps =
  | {
      multiple: true;
      options: Option[];
      value: number[];
      onChange: (val: number[]) => void;
      label: string;
      disabled?: boolean;
    }
  | {
      multiple?: false;
      options: Option[];
      value: number | null;
      onChange: (val: number | null) => void;
      label: string;
      disabled?: boolean;
    };

export default function SelectWithSearchModal(props: SelectWithSearchModalProps) {
  const { options, label, disabled } = props;
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  // Internal draft state (committed only on Valider)
  const [draftMulti, setDraftMulti] = useState<number[]>([]);
  const [draftSingle, setDraftSingle] = useState<number | null>(null);

  const filteredOptions = options.filter((o) =>
    o.label.toLowerCase().includes(search.toLowerCase())
  );

  const handleOpen = () => {
    if (disabled) return;
    if (props.multiple) {
      setDraftMulti(props.value ?? []);
    } else {
      setDraftSingle(props.value ?? null);
    }
    setSearch('');
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSearch('');
  };

  const handleConfirm = () => {
    if (props.multiple) {
      props.onChange(draftMulti);
    } else {
      props.onChange(draftSingle);
    }
    setOpen(false);
    setSearch('');
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (props.multiple) {
      props.onChange([]);
    } else {
      props.onChange(null);
    }
  };

  // Display value in the field
  const displayValue = (() => {
    if (props.multiple) {
      if (!props.value || props.value.length === 0) return '';
      return options
        .filter((o) => props.value.includes(o.value))
        .map((o) => o.label)
        .join(', ');
    } else {
      if (props.value === null || props.value === undefined) return '';
      return options.find((o) => o.value === props.value)?.label ?? '';
    }
  })();

  const hasValue = props.multiple ? (props.value?.length ?? 0) > 0 : props.value !== null;

  const handleToggleSelectAll = () => {
    if (draftAllSelected) {
      setDraftMulti([]);
    } else {
      setDraftMulti(options.map((o) => o.value));
    }
  };

  const draftAllSelected = draftMulti.length === options.length && options.length > 0;

  return (
    <>
      <Box sx={{ position: 'relative' }}>
        <FormControl fullWidth size="small" disabled={disabled}>
          <InputLabel shrink>{label}</InputLabel>
          <OutlinedInput
            notched
            label={label}
            readOnly
            value={displayValue}
            onClick={handleOpen}
            inputProps={{ style: { cursor: disabled ? 'default' : 'pointer' } }}
            sx={{ cursor: disabled ? 'default' : 'pointer', pr: hasValue ? '40px' : undefined }}
            endAdornment={
              hasValue ? (
                <InputAdornment position="end" sx={{ mr: '-6px' }}>
                  <IconButton size="small" onClick={handleClear} disabled={disabled}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined
            }
          />
        </FormControl>
      </Box>

      <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>{label}</DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
            <TextField
              autoFocus
              fullWidth
              size="small"
              placeholder={t('search')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: search ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearch('')}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : undefined
              }}
            />
          </Box>

          {props.multiple && (
            <Box sx={{ px: 2, pb: 0.5 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={draftAllSelected}
                    indeterminate={draftMulti.length > 0 && !draftAllSelected}
                    onChange={handleToggleSelectAll}
                    size="small"
                  />
                }
                label={t('referencesTables.allClients')}
              />
            </Box>
          )}

          <List dense disablePadding sx={{ maxHeight: 300, overflowY: 'auto' }}>
            {props.multiple ? (
              filteredOptions.map((option) => {
                const checked = draftMulti.includes(option.value);
                return (
                  <ListItemButton
                    key={option.value}
                    dense
                    onClick={() => {
                      setDraftMulti((prev) =>
                        checked ? prev.filter((v) => v !== option.value) : [...prev, option.value]
                      );
                    }}
                  >
                    <Checkbox checked={checked} size="small" sx={{ p: 0.5, mr: 1 }} />
                    <ListItemText primary={option.label} />
                  </ListItemButton>
                );
              })
            ) : (
              <RadioGroup
                value={draftSingle ?? ''}
                onChange={(e) => setDraftSingle(Number(e.target.value))}
              >
                {filteredOptions.map((option) => (
                  <ListItemButton
                    key={option.value}
                    dense
                    onClick={() => setDraftSingle(option.value)}
                  >
                    <Radio value={option.value} size="small" sx={{ p: 0.5, mr: 1 }} />
                    <ListItemText primary={option.label} />
                  </ListItemButton>
                ))}
              </RadioGroup>
            )}
            {filteredOptions.length === 0 && (
              <Box sx={{ px: 2, py: 2, color: 'text.secondary', fontSize: '0.875rem' }}>
                {t('noResultsFound')}
              </Box>
            )}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{t('referencesTables.cancel')}</Button>
          <Button variant="contained" onClick={handleConfirm}>
            {t('confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
